import { FieldValues } from 'react-hook-form';
import {
  useCheckboxFieldController,
  UseCheckboxFieldControllerProps,
} from '../../../hooks/form/useCheckboxFieldController';
import {
  MuiFormControl,
  MuiFormHelperText,
} from '../../../../../modules/material';
import Checkbox, { CheckboxProps } from '../../library/Checkbox';
import ErrorMessage from '../shared/ErrorMessage';

type CheckboxPropsWithControllerProps<TFieldValues> = Omit<
  CheckboxProps,
  'value' | 'name' | 'defaultValue' | 'disabled' | 'onChange'
> &
  UseCheckboxFieldControllerProps<TFieldValues>;

export interface CheckboxFieldProps<TFieldValues>
  extends CheckboxPropsWithControllerProps<TFieldValues> {
  // "nativeDisabled" visually disables checkboxes without resetting the field value
  nativeDisabled?: boolean;
  errorMessageDisabled?: boolean;
  fullWidth?: boolean;
}

export default function CheckboxField<TFieldValues extends FieldValues>(
  props: CheckboxFieldProps<TFieldValues>,
) {
  const {
    name,
    value,
    multiple,
    onChange,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    nativeDisabled,
    errorMessageDisabled,
    groupName,
    fullWidth = true,
    ...restCheckboxProps
  } = props;

  const {
    field,
    isChecked,
    onChangeField,
    required,
    formState: { errors },
    fieldState: { invalid: error },
  } = useCheckboxFieldController({
    name,
    value,
    multiple,
    onChange,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    groupName,
  });

  return (
    <MuiFormControl error={error} fullWidth={fullWidth}>
      <>
        <Checkbox
          name={field.name}
          inputRef={field.ref}
          value={value}
          disabled={nativeDisabled || disabled}
          required={required}
          checked={isChecked}
          onChange={onChangeField}
          {...restCheckboxProps}
        />
        {errorMessageDisabled ||
          (error && (
            <MuiFormHelperText>
              <ErrorMessage name={field.name} errors={errors} />
            </MuiFormHelperText>
          ))}
      </>
    </MuiFormControl>
  );
}
